/* COY Reveal — the single shared reveal contract (Stage 0 of the unification).
 *
 * Contract (canonical names adopted from the vendor theme, not invented):
 *   - marker/state: elements are revealed by adding the `animated` class; section CSS keys
 *     its existing choreography (staggers, per-card delays) off that class, unchanged.
 *   - registration: JS calls CoyReveal.observe(el). Sections keep their own CSS; only the
 *     observer machinery is shared.
 *   - replay opt-in: an element carrying `data-reveal-replay` (or inside an ancestor that
 *     does) is reset to its pre-reveal state on FULL exit from the viewport and stays
 *     observed, so returning to view replays the reveal. Everything else reveals once,
 *     settles, and is unobserved. The default lives here, in one place.
 *
 * Deterministic lifecycle:
 *   - in viewport at load: IntersectionObserver fires immediately on observe() for
 *     already-intersecting targets -> reveal at once, settle.
 *   - first entry from below: reveal once on first intersection, settle.
 *   - replay sections: threshold 0 means the leave callback only fires at
 *     intersectionRatio === 0 — a genuine full exit. Partial scrolls never reset,
 *     so nothing feels twitchy.
 *   - scripts disabled: reveal CSS is gated on html.no-js elsewhere (content is never
 *     hidden without JS); this file is pure enhancement.
 *
 * One observer per rootMargin (custom margins via data-reveal-margin still share
 * instances). Defaults preserve the previous trigger geometry: ScrollTrigger used
 * start "top 95%" on mobile and "top 90%" on desktop; the equivalent rootMargins are
 * -5% / -10% bottom. The margin is chosen at observe() time; crossing the 749px
 * boundary later keeps the original margin (a 5% trigger-line difference, accepted
 * for simplicity — no reveal is ever lost).
 *
 * Compositor-only by contract: the CSS this drives animates transform/opacity only.
 */
(function () {
  'use strict';
  if (window.CoyReveal) return;

  var MARGIN_MOBILE = '0px 0px -5% 0px';
  var MARGIN_DESKTOP = '0px 0px -10% 0px';
  var mobile = window.matchMedia('(max-width: 749px)');

  var observers = {};
  var exitObserver = null;

  function isReplay(el) {
    return (
      el.hasAttribute('data-reveal-replay') ||
      !!(el.closest && el.closest('[data-reveal-replay]'))
    );
  }

  /* ENTER observer(s): margin-shrunk viewport so reveals start slightly before the true
     edge (parity with the previous ScrollTrigger start lines). Reveal-only: resets never
     happen here — verification caught that using this shrunk geometry for exits resets an
     element that is still visibly on screen inside the margin band. */
  function handleEnter(margin) {
    return function (entries) {
      for (var i = 0; i < entries.length; i++) {
        var entry = entries[i];
        if (!entry.isIntersecting) continue;
        var el = entry.target;
        el.classList.add('animated');
        if (!isReplay(el)) observers[margin].unobserve(el); /* settled forever */
      }
    };
  }

  /* EXIT observer: TRUE viewport (no rootMargin), threshold 0 — a not-intersecting entry
     here means intersectionRatio 0 against the real viewport: a genuine full exit, never a
     partial scroll. Only replay elements are registered on it. */
  function handleExit(entries) {
    for (var i = 0; i < entries.length; i++) {
      var entry = entries[i];
      if (
        !entry.isIntersecting &&
        entry.intersectionRatio === 0 &&
        entry.target.classList.contains('animated')
      ) {
        entry.target.classList.remove('animated'); /* arm the replay */
      }
    }
  }

  function getEnterObserver(margin) {
    if (!observers[margin]) {
      observers[margin] = new IntersectionObserver(handleEnter(margin), {
        rootMargin: margin,
        threshold: 0,
      });
    }
    return observers[margin];
  }

  function observe(el) {
    if (!el || el.__coyRevealWired) return; /* idempotent: section:load safe */
    el.__coyRevealWired = true;
    var margin =
      el.getAttribute('data-reveal-margin') ||
      (mobile.matches ? MARGIN_MOBILE : MARGIN_DESKTOP);
    getEnterObserver(margin).observe(el);
    if (isReplay(el)) {
      if (!exitObserver) {
        exitObserver = new IntersectionObserver(handleExit, { threshold: 0 });
      }
      exitObserver.observe(el);
    }
  }

  /* ------------------------------------------------------------------
     Geometry mode (Stage 4, evaluation fixed): exact ScrollTrigger-
     equivalent trigger lines. The per-section reveals used element-
     relative start lines ("50% 90%", "bottom 95%", "20% bottom"...) that
     rootMargin alone cannot express. The condition itself never changed:
       reveal when rect.top + rect.height*elFrac <= vh*vpFrac.
     WHEN it gets evaluated is the hard part. IO thresholds are fractions
     of the ELEMENT's visible area while the condition is a VIEWPORT line,
     and between two threshold crossings IO stays silent:
       - a SHORT element (a subheading) becomes fully visible near the
         bottom edge, then produces NO further callbacks until it exits
         at the TOP — so a line it crossed mid-viewport was only noticed
         when the heading touched the top edge (owner-visible bug);
       - a TALL container's 25%-visibility steps are similarly far apart.
     No threshold density fixes both, so thresholds are only the coarse
     wake-up. The evaluator is a shared rAF-coalesced pass over the small
     pending registry, driven by capture-phase scroll (capture catches
     the body-scroller templates and inner scrollers — scroll events do
     not bubble) and by resize. Reads only (getBoundingClientRect), no
     writes between reads, so no layout thrash; elements retire on fire
     and the listeners detach when the registry empties.
     `target` lets the observed element differ from the element receiving
     the class (product-markers observes the image, reveals the section).
     Reveal-once only: geometry elements settle and are unobserved.
     ------------------------------------------------------------------ */
  var geomObserver = null;
  var geomRegistry = [];
  var geomTickScheduled = false;
  var geomScrollWired = false;

  function geomEvalAll() {
    geomTickScheduled = false;
    var vh = window.innerHeight;
    for (var r = geomRegistry.length - 1; r >= 0; r--) {
      var reg = geomRegistry[r];
      var rect = reg.el.getBoundingClientRect();
      if (rect.top + rect.height * reg.elFrac <= vh * reg.vpFrac) {
        reg.target.classList.add('animated');
        if (geomObserver) geomObserver.unobserve(reg.el);
        geomRegistry.splice(r, 1);
      }
    }
    if (!geomRegistry.length && geomScrollWired) {
      geomScrollWired = false;
      window.removeEventListener('scroll', geomTick, true);
      window.removeEventListener('resize', geomTick, true);
    }
  }

  function geomTick() {
    if (geomTickScheduled || !geomRegistry.length) return;
    geomTickScheduled = true;
    requestAnimationFrame(geomEvalAll);
  }

  function observeAt(el, elFrac, vpFrac, target) {
    if (!el || el.__coyRevealGeomWired) return; /* idempotent */
    el.__coyRevealGeomWired = true;
    if (!geomObserver) {
      /* IO is the wake-up for the non-scroll cases: the initial callback
         (elements already past their line at wire time) and layout that
         moves things without a scroll (image loads, section injection). */
      geomObserver = new IntersectionObserver(geomTick, {
        threshold: [0, 0.25, 0.5, 0.75, 1],
      });
    }
    geomRegistry.push({
      el: el,
      elFrac: elFrac || 0,
      vpFrac: vpFrac == null ? 1 : vpFrac,
      target: target || el,
    });
    geomObserver.observe(el);
    if (!geomScrollWired) {
      geomScrollWired = true;
      window.addEventListener('scroll', geomTick, true);
      window.addEventListener('resize', geomTick, true);
    }
    geomTick();
  }

  /* ------------------------------------------------------------------
     Media fade (Stage 1). LOAD-driven, not viewport-driven: an image
     fades in when its pixels are ready. Canonical state class kept:
     `is-loaded` (base.css transitions img opacity on it; eager images
     and the failsafe animations in base.css are the style layer of the
     same contract and are untouched). This replaces the inline fadeIn
     script that lived in theme.liquid — one home for reveal machinery.
     Additions over the old inline version (missing-guard fixes only):
     `error` also settles the image (a broken image must never hold
     opacity:0 forever) and listeners are { once: true }.
     ------------------------------------------------------------------ */
  function markLoaded(img) {
    /* `is-loaded` = the contract's one canonical media state class (Stage 5: the coy-img-ready
       migration alias is retired; every consumer now reads is-loaded directly). */
    img.classList.add('is-loaded');
  }

  function wireImg(img) {
    if (!img || img.__coyMediaWired) return;
    img.__coyMediaWired = true;
    if (img.complete) {
      markLoaded(img);
    } else {
      img.addEventListener('load', function () { markLoaded(img); }, { once: true });
      img.addEventListener('error', function () { markLoaded(img); }, { once: true });
    }
  }

  /* Videos (Stage 2, moved from coy-ap-behaviour.js verbatim): ready when playable.
     readyState >= 2 = HAVE_CURRENT_DATA, the same threshold the old wiring used. */
  function wireVideo(video) {
    if (!video || video.__coyMediaWired) return;
    video.__coyMediaWired = true;
    if (video.readyState >= 2) {
      video.classList.add('coy-video-ready');
    } else {
      video.addEventListener('canplay', function () {
        video.classList.add('coy-video-ready');
      }, { once: true });
    }
  }

  function wireMedia() {
    document.querySelectorAll('img').forEach(wireImg);
    document.querySelectorAll('video').forEach(wireVideo);
    new MutationObserver(function (mutations) {
      for (var m = 0; m < mutations.length; m++) {
        var added = mutations[m].addedNodes;
        for (var n = 0; n < added.length; n++) {
          var node = added[n];
          if (node.tagName === 'IMG') wireImg(node);
          if (node.tagName === 'VIDEO') wireVideo(node);
          if (node.querySelectorAll) {
            node.querySelectorAll('img').forEach(wireImg);
            node.querySelectorAll('video').forEach(wireVideo);
          }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wireMedia);
  } else {
    wireMedia();
  }

  window.CoyReveal = { observe: observe, observeAt: observeAt };
})();